import { inject, Injectable } from '@angular/core';
import { AppConfig, buildApiUrl } from '@app/app.config';
import { catchError, finalize, forkJoin, map, Observable, of, tap } from 'rxjs';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { BulkTaxesResponse, CancelPendingShipmentBody, CancelPendingShipmentResponse, CancelResponse, formatPickitPointSchedule, formatPriceAr, getTodayDate, LabelResponse, Labels, ModalSizes, PaymentResponseVerify, PdfResponse, PickitPoints, PickupInfoRequest, QuoteResponse, QuoteWithPackagesRequest, ScheduleTranslations, ShipmentCreditAccountRequest, ShipmentCreditAccountResponse, ShipmentPaymentStatus, ShipmentResponse, ShipmentsWithPackagesRequest, ShipmentsWithPackagesResponse, ValidateHomePickupResponse } from './utils-shipments';
import { TranslateService } from '@ngx-translate/core';
import { CategoryParam, DataCard, DataCardExtension, DigitalReceiptStatus, formatDate, GroupParam, QueryParamsShipments, ResponseShipmentsCard, ShippingDelivery, TypeCardEnum, TypeShipment, StatusShipment, ModalConfirmData, ConfirmModalResult, ResponseListShipments } from '@app/shared/components/utils';
import { ConfirmModalComponent } from '@app/shared/components/confirm-modal/confirm-modal.component';
import { IMAGES } from '@app/shared/constants/images';
import { MatDialog } from '@angular/material/dialog';
import { UserInfoService } from '@app/shared/services/user-info.service';
import { normalizeCountry } from './utils-shipments';

@Injectable({
  providedIn: 'root'
})
export class ShipmentsService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);
  private userInfoService = inject(UserInfoService);
  private dataCardExtensionCache = new Map<string, DataCardExtension>();
  private dialog = inject(MatDialog);
  private digitalReceiptDownloadsInProgress = new Set<number>();

  readonly ERROR_NOT_FOUND_INVOICE: ModalConfirmData = {
    image: IMAGES.error.exclamation,
    option: 'errorInvoiceNotFound',
    secondButton: false,
    firstButton: false
  };

  readonly ERROR_NOT_FOUND_RECEIPT: ModalConfirmData = {
    image: IMAGES.error.exclamation,
    option: 'cancelReceiptError',
    secondButton: false,
    firstButton: false
  }

  readonly ERROR_DOWNLOAD: ModalConfirmData = {
    image: IMAGES.success.info,
    option: 'errorDowndload',
    secondButton: true,
    firstButton: false
  }

  readonly ERROR_DIGITAL_RECEIPT: ModalConfirmData = {
    image: IMAGES.warning.exclamation,
    option: 'digitalReceiptUnavailable',
    secondButton: true,
    firstButton: false
  };

  private determineShipmentStatus(item: ResponseShipmentsCard): StatusShipment {
    // Estado determinado por cancelacion de usuario
    if (item.shipmentStatus === StatusShipment.canceled) {
      return StatusShipment.canceled;
    }

    // Estado determinado por Ocasa
    if (item.ocasaStatus) {
      return item.ocasaStatus as StatusShipment;
    }
    
    if (item.shipmentStatus === ShipmentPaymentStatus.creditAccount) {
      return StatusShipment.confirmed;
    }

    // Estado determinado por Pago
    if (item.paymentStatus === ShipmentPaymentStatus.paid) {
      return StatusShipment.confirmed;
    }

    if (item.paymentStatus === ShipmentPaymentStatus.pending) {
      return StatusShipment.toPay;
    }

    return StatusShipment.pending;
  }

  private determineTypeStatus(status: ShipmentPaymentStatus | StatusShipment): StatusShipment {
    switch (status) {
      case ShipmentPaymentStatus.paid:
        return StatusShipment.confirmed;
      case ShipmentPaymentStatus.creditAccount:
        return StatusShipment.confirmed;
      case ShipmentPaymentStatus.pending:
        return StatusShipment.pending;
      case ShipmentPaymentStatus.toPay:
        return StatusShipment.toPay;
      default:
        return status as StatusShipment;
    }
  }

  getListShipments(category: string, group: string): Observable<DataCard[]> {
    let params = new HttpParams()
      .set(QueryParamsShipments.category, category)
      .set(QueryParamsShipments.group, group);

    if (group === GroupParam.completed) {
      params = params.set('last-days', 90);
    }
    return this.http.get<ResponseListShipments>(buildApiUrl(AppConfig.shipmentList), {
      withCredentials: true,
      params
    }).pipe(
      tap((data) => {
        if (data?.creditAccountInfo) {
          this.userInfoService.updateCreditAccountInfo(data.creditAccountInfo);
        }
      }),
      map((data) => {
        const cards = data?.cards ?? [];
        if (cards.length === 0) {
          return [];
        }

        return cards.map((item): DataCard => {
          return {
            id: item.id,
            visualId: item.isQuote ? `PP${item.id}` : item.id.toString(),
            isQuote: item.isQuote,
            typeCard: item.isReturn ? TypeCardEnum.return : TypeCardEnum.shipment,
            completedName: item.destinationName,
            idTracking: item.trackingCode,
            collectionDate: item.statusTimestamp ?? '',
            origin: item.originAddress,
            originName: item.originName,
            status: this.determineShipmentStatus(item),
            deliveryDate: item.ocasaStatus === StatusShipment.delivered ? item.statusTimestamp : undefined,
            dateCreated: item.creationTimestamp ? item.creationTimestamp : '',
            totalCost: formatPriceAr(item.price),
            shippingDelivery: item.shipmentType as ShippingDelivery,
            destination: item.destinationAddress,
            isSendEmail: item.labelStatus === 'Sent' ? true : false,
            dateStatus: item.statusTimestamp ? item.statusTimestamp : '',
            confirmDate: item.statusTimestamp ? formatDate(item.statusTimestamp) : '',
            externalPlatform: item.externalPlatform,
            isManual: item.isManual,
            digitalReceiptStatus: item.digitalReceiptStatus ?? null
          }
        });
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  getShipmentsOnTransit(): Observable<DataCard[]> {
    const shipments$ = this.getListShipments(CategoryParam.shipment, GroupParam.in_transit);
    const returns$ = this.getListShipments(CategoryParam.return, GroupParam.in_transit);

    return forkJoin([shipments$, returns$]).pipe(
      map(([shipments, returns]) => [...shipments, ...returns])
    );
  }

  getPickitsPoint(postalCode: string, date: string, province: string, pickitPointId: string = "", isDropOff: boolean = false): Observable<{pickitPoints: PickitPoints[]}> {
    const params = new HttpParams()
    .set('postal-code', postalCode)
    .set('province', province)
    .set('shipment-date', date)
    .set('is-dropoff', isDropOff ? 'true' : 'false')
    .set('pp-id', pickitPointId);

    return this.http.get<{pickitPoints: PickitPoints[]}>(buildApiUrl(AppConfig.pickitPoints), {
      params: params,
      withCredentials: true
    });
  }

  calculateCost(body: QuoteWithPackagesRequest): Observable<QuoteResponse> {
    return this.http.post<QuoteResponse>(buildApiUrl(AppConfig.shipmentQuoteWithProducts), body, {
      withCredentials: true
    });
  }

  calculateCostWithProducts(body: QuoteWithPackagesRequest): Observable<QuoteResponse> {
    return this.calculateCost(body);
  }

  sendShipment(body: PickupInfoRequest): Observable<ShipmentResponse> {
    return this.http.post<ShipmentResponse>(buildApiUrl(AppConfig.shipments), body, {
      withCredentials: true
    });
  }

  sendShipmentsWithPackages(body: ShipmentsWithPackagesRequest): Observable<ShipmentsWithPackagesResponse> {
    const payload: ShipmentsWithPackagesRequest = {
      ...body,
      shipments: body.shipments.map(shipment => ({
        ...shipment,
        origin: {
          ...shipment.origin,
          country: normalizeCountry(shipment.origin.country)
        }
      }))
    };

    return this.http.post<ShipmentsWithPackagesResponse>(buildApiUrl(AppConfig.shipmentsWithPackages), payload, {
      withCredentials: true
    });
  }

  getShipmentByTrackingCode(trackingCode: string): Observable<PickupInfoRequest> {
    const params = new HttpParams()
    .set('tracking-code', trackingCode);

    return this.http.get<PickupInfoRequest>(buildApiUrl(AppConfig.shipments), {
      withCredentials: true,
      params: params
    });
  }

  getShipmentById(id: string): Observable<PickupInfoRequest> {
    const params = new HttpParams()
    .set('id', id);

    return this.http.get<PickupInfoRequest>(buildApiUrl(AppConfig.shipments), {
      withCredentials: true,
      params: params
    });
  }

  getShipmentQuoteById(id: string): Observable<PickupInfoRequest> {
    return this.http.get<PickupInfoRequest>(buildApiUrl(AppConfig.shipmentQuoteById + `/${id}`), {
      withCredentials: true
    });
  }

  generateLabel(body: Labels[]): Observable<LabelResponse[]> {
    return this.http.post<LabelResponse[]>(buildApiUrl(AppConfig.labels), body, {
      withCredentials: true
    });
  }

  downloadLabelsAsZip(ids: number[]): Observable<{blob: Blob, successCount: number, failedCount: number, totalCount: number}> {
    const body = { ids };
    return this.http.post(buildApiUrl(AppConfig.ziplabels), body, {
      responseType: 'blob',
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map((response: HttpResponse<Blob>) => {
        const successCount = Number(response.headers.get('X-PDF-Success-Count')) || 0;
        const failedCount = Number(response.headers.get('X-PDF-Failed-Count')) || 0;
        const totalCount = ids.length;

        return {
          blob: response.body!,
          successCount,
          failedCount,
          totalCount
        };
      })
    );
  }

  getFormattedPickitPoints(zipcode: string, province: string, pickitPointId: string = "", isDropOff: boolean = false): Observable<PickitPoints[]> {
    const date = getTodayDate('-');
    const translations: ScheduleTranslations = {
      days: this.translate.instant('product-form.days'),
      connector: this.translate.instant('product-form.schedule')
    };

    return this.getPickitsPoint(zipcode, date, province, pickitPointId, isDropOff).pipe(
      map(response => {
        if (!response) return [];

        return response.pickitPoints.map((point: PickitPoints) => ({
          ...point,
          formattedSchedule: formatPickitPointSchedule(point.schedule, translations)
        }));
      }),
      catchError(() => of([]))
    );
  }

  getShipmentForDataCard(trackingCode: string, id: number, externalPlatform: string | null): Observable<DataCardExtension> {
    return this.getShipmentByTrackingCode(trackingCode).pipe(
      map(shipment => this.mapShipmentToDataCard(shipment, false, externalPlatform)),
      catchError(() => {
        return this.getShipmentById(id.toString()).pipe(
          map(altShipment => this.mapShipmentToDataCard(altShipment, false, externalPlatform))
        );
      })
    );
  }

  getShipmentQuoteForDataCard(id: number, visualId: string, externalPlatform: string | null): Observable<DataCardExtension> {
    const cachedData = this.dataCardExtensionCache.get(visualId);
    if (cachedData) {
      return of(cachedData);
    }

    return this.getShipmentQuoteById(id.toString()).pipe(
      map(shipment => this.mapShipmentToDataCard(shipment, true, externalPlatform))
    );
  }

  private mapShipmentToDataCard(shipment: PickupInfoRequest, isQuote: boolean = false, externalPlatform: string | null): DataCardExtension {
    const date = shipment.detail.pickupDate ? formatDate(shipment.detail.pickupDate) : '';
    const status = shipment.detail.status;
    const isReturn = shipment.detail.isReturn;
    const dataCardExtension: DataCardExtension = {
      id: shipment.detail.id!,
      visualId: isQuote ? `PP${shipment.detail.id}` : shipment.detail.id!.toString(),
      isQuote,
      typeStatus: this.determineTypeStatus(status ? status as StatusShipment : ShipmentPaymentStatus.pending),
      name: shipment.destination.name,
      idTracking: shipment.detail.trackingCode ? shipment.detail.trackingCode : '',
      productName: shipment.detail.packages.map(packageItem => packageItem.name).join(', '),
      status: shipment.detail.status!,
      contact: {
        name: isReturn ? shipment.origin.name : shipment.destination.name,
        email: isReturn ? (shipment.origin.email ?? '') : (shipment.destination.email ?? ''),
        phone: isReturn ? (shipment.origin.phone ?? '') : (shipment.destination.phone ?? '')
      },
      addressOrigin: {
        street: shipment.origin.street,
        number: shipment.origin.streetNumber,
        city: shipment.origin.city,
        province: shipment.origin.province,
        country: shipment.origin.country ? shipment.origin.country : ''
      },
      addressDestination: {
        street: shipment.destination.street,
        number: shipment.destination.streetNumber,
        city: shipment.destination.city,
        province: shipment.destination.province,
        country: shipment.destination.country ? shipment.destination.country : ''
      },
      shipment: {
        typeShipment: isReturn ? TypeShipment.return : TypeShipment.shipment,
        price: shipment.detail.valueToCollect ? shipment.detail.valueToCollect.toString() : '',
        date: date
      },
      isReturn,
      externalPlatform,
      digitalReceiptStatus: (shipment.detail.digitalReceiptStatus as DigitalReceiptStatus | null | undefined) ?? null
    };
    this.dataCardExtensionCache.set(dataCardExtension.visualId, dataCardExtension);
    return dataCardExtension;
  }

  postPaymentForm(ids: number[], paymentId?: string): Observable<string>{
    const body = { shipmentQuoteIds: ids, paymentId};
    return this.http.post(buildApiUrl(AppConfig.getPayment), body, {
      responseType: 'text',
      withCredentials:true
    });
  }

  getInvoicePdf(id: string | null, saleId: string | null, retry: boolean = false) {
    let params = new HttpParams();
    if (id) params = params.set('shipment-id', id);
    if (saleId) params = params.set('sale-id', saleId);
    this.http.get<PdfResponse>(buildApiUrl(`${AppConfig.getInvoice}`), {
      withCredentials: true,
      params: params
    }).pipe(
      catchError(() => {
        if (retry && saleId) {
          this.showErrorDownload(saleId, 'invoice');
        } else {
          this.showErrorInvoiceNotFound();
        }
        return of(null);
      })
    ).subscribe((response: PdfResponse | null) => {
      if (response && response.pdfUrl) {
        window.open(response.pdfUrl, '_blank');
      }
    });
  }
  
  /**
   * Descarga el PDF de la constancia digital de entrega. Ante cualquier error muestra un modal genérico.
   * Emite true si se descargó.
   */
  downloadDigitalReceipt(shipmentId: number, idTracking: string): Observable<boolean> {
    // Evita un segundo request del mismo envío mientras el primero no respondió (p. ej. desde la card y el panel)
    if (this.digitalReceiptDownloadsInProgress.has(shipmentId)) {
      return of(false);
    }
    this.digitalReceiptDownloadsInProgress.add(shipmentId);

    return this.http.get(buildApiUrl(AppConfig.digitalReceipt(shipmentId)), {
      responseType: 'blob',
      withCredentials: true
    }).pipe(
      map((blob: Blob) => {
        saveAs(blob, `Constancia_${idTracking}.pdf`);
        return true;
      }),
      catchError(() => {
        this.dialog.open(ConfirmModalComponent, {
          width: ModalSizes.large,
          data: this.ERROR_DIGITAL_RECEIPT,
          disableClose: true
        });
        return of(false);
      }),
      finalize(() => this.digitalReceiptDownloadsInProgress.delete(shipmentId))
    );
  }

  cancelShipmentQuoteOrCreditAccount(ids: number[], quoteIds: number[]): Observable<CancelResponse[]> {
    const body = { ids, quoteIds };
    return this.http.post<CancelResponse[]>(buildApiUrl(AppConfig.cancelShipmentQuoteOrCreditAccount), body, {
      withCredentials: true
    });
  }

  cancelPendingShipment(body: CancelPendingShipmentBody): Observable<CancelPendingShipmentResponse> {
    return this.http.post<CancelPendingShipmentResponse>(buildApiUrl(AppConfig.cancelPendingShipment), body, {
      withCredentials: true
    });
  }

  getReceiptPdf(id: string | null, saleId: string | null, retry: boolean = false){
    let params = new HttpParams();
    if (id) params = params.set('shipment-id', id);
    if (saleId) params = params.set('sale-id', saleId);

    this.http.get<PdfResponse>(buildApiUrl(`${AppConfig.getReceipt}`), {
      withCredentials:true,
      params: params
    }).pipe(
      catchError(() => {
        if (retry && saleId) {
          this.showErrorDownload(saleId, 'receipt');
        } else {
          this.showErrorReceiptNotFound();
        }
        return of(null);
      })
    ).subscribe((response: PdfResponse | null) => {
      if (response && response.pdfUrl) {
        window.location.href = response.pdfUrl;
      }
    });
  }

  postBulkTaxes(ids: number[]): Observable<BulkTaxesResponse>{
    const body = { quoteIds: ids };
    return this.http.post<BulkTaxesResponse>(buildApiUrl(AppConfig.postBulkTaxes), body, {
      withCredentials: true
    });
  }

  verifyPayment(shipmentQuoteId: string | null, saleId: string | null): Observable<PaymentResponseVerify>{
    let body;
    if (shipmentQuoteId) {
      body = { shipmentQuoteId };
    } else if (saleId) {
      body = { saleId };
    }
    return this.http.post<PaymentResponseVerify>(buildApiUrl(AppConfig.verifyPayment), body, {
      withCredentials: true
    });
  }

  paymentWithCreditAccount(body: ShipmentCreditAccountRequest): Observable<ShipmentCreditAccountResponse> {
    return this.http.post<ShipmentCreditAccountResponse>(buildApiUrl(AppConfig.creditAccountPayment), body, {
      withCredentials: true
    });
  }

  postValidateHomePickup(ids: number[], quoteIds: number[]){
    const body = { ids, quoteIds };
    return this.http.post<ValidateHomePickupResponse>(buildApiUrl(AppConfig.postValidateHomePickup), body, {
      withCredentials: true
    });
  }

  clear() {
    this.dataCardExtensionCache.clear();
  }

  private showErrorInvoiceNotFound(){
    this.dialog.open(ConfirmModalComponent, {
      width: ModalSizes.large,
      data: this.ERROR_NOT_FOUND_INVOICE,
      disableClose: true
    });
  }

  private showErrorReceiptNotFound(){
    this.dialog.open(ConfirmModalComponent, {
      width: ModalSizes.large,
      data: this.ERROR_NOT_FOUND_RECEIPT,
      disableClose: true
    });
  }

  private showErrorDownload(saleId: string, document: string){
    const response = this.dialog.open(ConfirmModalComponent, {
      width: ModalSizes.large,
      data: this.ERROR_DOWNLOAD,
      disableClose: true
    });

    response.afterClosed().subscribe((result: ConfirmModalResult) => {
      if (result && result.click == 'filled') {
        if (document === 'invoice') {
          this.getInvoicePdf(null, saleId, true);
        } else if (document === 'receipt') {
          this.getReceiptPdf(null, saleId, true);
        }
      }
    });
  }
}
